import { Zap, RefreshCw, Ticket, Copy, ShieldCheck, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const GATEWAY_LABEL = { viva: "Viva Wallet", revolut: "Revolut" };

function fmtDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

function daysLeft(d) {
  if (!d) return null;
  const ms = new Date(d).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / 86400000));
}

export function PortalPrimeCard({ restaurantId, subscription, coupon, offers = [], onCancel }) {
  const navigate = useNavigate();

  const renewal = fmtDate(subscription?.renewal_date);
  const gateway = GATEWAY_LABEL[subscription?.payment_gateway] || subscription?.payment_gateway || "";
  const paymentConfirmed = subscription?.payment_confirmed === true;
  const couponUsable = coupon?.active === true && coupon?.used !== true;
  const couponExpiry = fmtDate(coupon?.expires_at);
  const couponDays = daysLeft(coupon?.expires_at);

  const useCoupon = () => {
    if (!coupon?.code) return;
    navigate(`/m/${restaurantId}?coupon=${encodeURIComponent(coupon.code)}`);
  };

  const copyCode = () => {
    if (!coupon?.code) return;
    navigator.clipboard.writeText(coupon.code)
      .then(() => toast.success("Código copiado"))
      .catch(() => toast.error("No se pudo copiar el código"));
  };

  return (
    <div className="space-y-3" data-testid="portal-prime-card">
      {/* Membership status */}
      <div className="rounded-2xl bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 text-white p-6 shadow-xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-5 h-5 text-yellow-300" />
              <span className="font-black text-xl tracking-tight">Kaiso Prime</span>
            </div>
            {renewal && (
              <p className="text-purple-200 text-xs flex items-center gap-1">
                <RefreshCw className="w-3 h-3" />Se renueva el {renewal}
              </p>
            )}
          </div>
          <Badge className="bg-yellow-400 text-yellow-900 border-0 font-bold text-xs px-2.5 py-1 shrink-0">
            ✓ Activo
          </Badge>
        </div>

        {paymentConfirmed && (
          <div className="flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2.5 text-xs" data-testid="prime-payment-confirmed">
            <ShieldCheck className="w-4 h-4 text-emerald-300 shrink-0" />
            <span className="font-bold">Pago confirmado</span>
            {gateway && <span className="text-purple-200">· {gateway}</span>}
            {subscription?.paid_at && (
              <span className="text-purple-300 ml-auto">{fmtDate(subscription.paid_at)}</span>
            )}
          </div>
        )}
      </div>

      {/* 7 EUR welcome coupon */}
      {coupon ? (
        <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-5" data-testid="prime-coupon">
          <div className="flex items-center gap-2 mb-2">
            <Ticket className="w-5 h-5 text-emerald-600" />
            <p className="font-black text-emerald-900">Cupón de bienvenida</p>
            {!couponUsable && (
              <Badge className="ml-auto bg-gray-200 text-gray-600 border-0 text-[10px]">
                {coupon.used ? "Usado" : "No disponible"}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 bg-white rounded-xl border border-emerald-200 px-3 py-2.5">
            <p className="font-mono font-black text-lg tracking-widest text-emerald-800 flex-1" data-testid="prime-coupon-code">
              {coupon.code}
            </p>
            <Button variant="ghost" size="sm" className="h-8 px-2 text-emerald-700" onClick={copyCode}>
              <Copy className="w-3.5 h-3.5" />
            </Button>
          </div>
          <ul className="mt-3 space-y-1 text-xs text-emerald-800">
            <li>• Descuento de 7 € en un solo pedido.</li>
            <li>• Pedido mínimo de {Number(coupon.min_order_value || 0).toFixed(0)} €.</li>
            <li>• Personal e intransferible.</li>
            <li>• No se acumula con otras promociones ni con productos en oferta.</li>
            {couponExpiry && (
              <li className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Válido hasta el {couponExpiry}
                {couponDays !== null && couponDays <= 7 && ` · quedan ${couponDays} día(s)`}
              </li>
            )}
          </ul>
          <Button
            className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-black h-11 rounded-xl"
            disabled={!couponUsable}
            data-testid="use-prime-coupon"
            onClick={useCoupon}
          >
            <Ticket className="w-4 h-4 mr-2" />Usar cupón de 7 €
          </Button>
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
          Tu cupón de bienvenida se activará en cuanto se confirme el pago.
        </div>
      )}

      {/* VIP offers — only what is really configured */}
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-2 px-0.5">
          Ofertas VIP
        </p>
        {offers.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white p-4 text-center" data-testid="prime-offers-empty">
            <p className="text-sm font-semibold text-gray-600">Ofertas VIP en preparación</p>
            <p className="text-xs text-gray-400 mt-1">
              Todavía no hay ofertas configuradas. Te avisaremos cuando estén disponibles.
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="prime-offers-list">
            {offers.map(offer => (
              <div key={offer.id} className="flex items-center gap-3 rounded-xl border border-purple-200 bg-purple-50 p-3">
                {offer.image_url
                  ? <img src={offer.image_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                  : <Zap className="w-6 h-6 text-purple-500 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-purple-900 leading-tight">{offer.product_name}</p>
                  <p className="text-xs text-purple-600 leading-tight">
                    <span className="line-through mr-1">{offer.normal_price.toFixed(2)} €</span>
                    <span className="font-black">{offer.prime_price.toFixed(2)} €</span>
                    {offer.expires_at && ` · hasta ${fmtDate(offer.expires_at)}`}
                  </p>
                  {offer.quantity_remaining !== null && offer.quantity_remaining !== undefined && (
                    <p className="text-[10px] text-purple-500">Quedan {offer.quantity_remaining} unidad(es)</p>
                  )}
                </div>
                <span className="text-[10px] font-semibold text-purple-600 bg-purple-100 px-2 py-1 rounded-full shrink-0">
                  −{offer.saving.toFixed(2)} €
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {onCancel && (
        <Button variant="ghost" className="w-full text-xs text-gray-400 hover:text-red-500" onClick={onCancel}>
          Cancelar suscripción PRIME
        </Button>
      )}
    </div>
  );
}
